import { useCallback, useEffect, useRef, useState } from 'react'

// Shared undo/redo history for designers whose entire document is ONE state
// value (visual/dashboard model, schema, business area). The hook observes the
// value and snapshots every distinct change, so ALL mutation paths (mutator
// funnels, AI generate, imports) are captured without touching call sites.
//
// Undo restores the previous snapshot via the designer's own setter; the
// restore itself is not recorded. Rapid successive edits (typing bursts)
// coalesce into a single undo step.

const MAX_HISTORY = 100
// Edits closer together than this collapse into one undo step, so typing a
// word is one step instead of one per keystroke.
const COALESCE_MS = 600

// A designer can be mounted above another (e.g. the Visual designer opened in
// an overlay from a dashboard tile, or the Schema editor inside the Visual
// designer). Instances register in mount order; only the topmost responds to
// the keyboard shortcuts so Ctrl+Z can't undo in two designers at once.
const instanceStack: symbol[] = []

export interface DesignHistory {
  canUndo: boolean
  canRedo: boolean
  undo: () => void
  redo: () => void
  /** Clear history and re-baseline on the next observed value. Call after a
   * load/replace — stale entries would restore a different/older object. */
  reset: () => void
}

export function useDesignHistory<T>(
  model: T,
  setModel: (next: T) => void,
  opts?: { disabled?: boolean },
): DesignHistory {
  const undoStackRef = useRef<string[]>([])
  const redoStackRef = useRef<string[]>([])
  // JSON of the last observed value; null = re-baseline on next observation.
  const lastJsonRef = useRef<string | null>(null)
  const lastEditAtRef = useRef(0)
  // True while the next observed change is our own undo/redo restore.
  const suppressRef = useRef(false)
  const [undoCount, setUndoCount] = useState(0)
  const [redoCount, setRedoCount] = useState(0)

  const setModelRef = useRef(setModel)
  setModelRef.current = setModel
  const disabledRef = useRef(!!opts?.disabled)
  disabledRef.current = !!opts?.disabled

  // Observe the value and record history on every distinct change.
  useEffect(() => {
    const json = JSON.stringify(model)
    if (lastJsonRef.current === null) {
      lastJsonRef.current = json
      return
    }
    if (json === lastJsonRef.current) {
      // A restore can land on JSON identical to the current one (edit cycles
      // coalesced back to their start) — the suppress flag is consumed here
      // too, or it would swallow the next real edit.
      suppressRef.current = false
      return
    }
    const prevJson = lastJsonRef.current
    lastJsonRef.current = json
    if (suppressRef.current) {
      suppressRef.current = false
      return
    }
    const now = Date.now()
    if (now - lastEditAtRef.current > COALESCE_MS || undoStackRef.current.length === 0) {
      undoStackRef.current.push(prevJson)
      if (undoStackRef.current.length > MAX_HISTORY) undoStackRef.current.shift()
    }
    lastEditAtRef.current = now
    redoStackRef.current = []
    setUndoCount(undoStackRef.current.length)
    setRedoCount(0)
  }, [model])

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return
    const prev = undoStackRef.current.pop()!
    if (lastJsonRef.current !== null) redoStackRef.current.push(lastJsonRef.current)
    suppressRef.current = true
    lastEditAtRef.current = 0
    setUndoCount(undoStackRef.current.length)
    setRedoCount(redoStackRef.current.length)
    try {
      setModelRef.current(JSON.parse(prev) as T)
    } catch (err) {
      console.error('Undo failed to restore snapshot', err)
      suppressRef.current = false
    }
  }, [])

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return
    const next = redoStackRef.current.pop()!
    if (lastJsonRef.current !== null) undoStackRef.current.push(lastJsonRef.current)
    suppressRef.current = true
    lastEditAtRef.current = 0
    setUndoCount(undoStackRef.current.length)
    setRedoCount(redoStackRef.current.length)
    try {
      setModelRef.current(JSON.parse(next) as T)
    } catch (err) {
      console.error('Redo failed to restore snapshot', err)
      suppressRef.current = false
    }
  }, [])

  const reset = useCallback(() => {
    undoStackRef.current = []
    redoStackRef.current = []
    lastJsonRef.current = null
    lastEditAtRef.current = 0
    suppressRef.current = false
    setUndoCount(0)
    setRedoCount(0)
  }, [])

  // Keyboard shortcuts: Cmd/Ctrl+Z undo, Cmd/Ctrl+Shift+Z or Ctrl+Y redo.
  // Skipped while typing in a field (input/textarea/select/contenteditable —
  // the latter covers CodeMirror) so text editing keeps its native undo.
  useEffect(() => {
    const id = Symbol('design-history')
    instanceStack.push(id)
    const onKey = (e: KeyboardEvent) => {
      if (disabledRef.current) return
      if (instanceStack[instanceStack.length - 1] !== id) return
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return
      const key = e.key.toLowerCase()
      if (key !== 'z' && key !== 'y') return
      const t = e.target as HTMLElement | null
      if (
        t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'
          || t.tagName === 'SELECT' || t.isContentEditable)
      ) return
      e.preventDefault()
      if (key === 'y' || e.shiftKey) redo()
      else undo()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      const i = instanceStack.indexOf(id)
      if (i >= 0) instanceStack.splice(i, 1)
    }
  }, [undo, redo])

  return { canUndo: undoCount > 0, canRedo: redoCount > 0, undo, redo, reset }
}
